import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { atomicConfig } from "../../../../../config";
import useCurrency from "../../../../../hooks/useCurrency";
import { isCookieAuth } from "../../../../../lib/authProvider";
import {
  fetchProductProfitabilityYearly,
  storeAnalyticsSelector,
} from "../../../../../redux/slices/store_analytics";
import { call_api_auth } from "../../../../../utils/services";
import { useStores } from "../shared/Storepicker";

import PnLByProduct from "./PnLByProduct";

const FETCH_ENDPOINTS = {
  profitability: "Profitability",
  productItems: "Product items",
};

function makeFetchKey(endpoint, storeValue, marketplace) {
  return `${endpoint}|${storeValue}|${marketplace}`;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const RAW_FIELDS = [
  "orderedProductSales",
  "item_promotion_discount",
  "refunded_amount",
  "referral_fee",
  "fulfillment_fee",
  "cogs_cost",
  "product_cost",
  "brand_cost",
  "display_cost",
  "extra_fee",
  "units",
  "refunds",
  "indirect_expenses",
  "estimated_payout",
  "real_acos",
  "refund_units",
  "sellable_returns",
];

const PNL_ROW_CONFIG = [
  {
    key: "sales",
    label: "Sales",
    field: "orderedProductSales",
    format: "currency",
    expandable: false,
  },
  {
    key: "units",
    label: "Units",
    field: "units",
    format: "number",
    expandable: false,
  },
  {
    key: "promo",
    label: "Promo",
    field: "item_promotion_discount",
    format: "currency_negative",
    negate: true,
    expandable: false,
  },
  {
    key: "advertising_cost",
    label: "Advertising cost",
    field: "_advertising_cost",
    format: "currency_negative",
    negate: true,
    expandable: true,
    subRows: [
      {
        label: "Product",
        field: "product_cost",
        format: "currency_negative",
        negate: true,
      },
      {
        label: "Brand",
        field: "brand_cost",
        format: "currency_negative",
        negate: true,
      },
      {
        label: "Display",
        field: "display_cost",
        format: "currency_negative",
        negate: true,
      },
    ],
  },
  {
    key: "refund_cost",
    label: "Refund cost",
    field: "refunded_amount",
    format: "currency_negative",
    negate: true,
    expandable: false,
  },
  {
    key: "amazon_fees",
    label: "Amazon fees",
    field: "_amazon_fees",
    format: "currency_negative",
    expandable: true,
    subRows: [
      {
        label: "Fulfillment fees",
        field: "fulfillment_fee",
        format: "currency_negative",
        negate: true,
      },
      {
        label: "Referral fees",
        field: "referral_fee",
        format: "currency_negative",
        negate: true,
      },
      {
        label: "Extra fees",
        field: "extra_fee",
        format: "currency_negative",
        dynamicBreakdownField: "_extra_fee_breakdown",
      },
    ],
  },
  {
    key: "cogs",
    label: "Cost of goods",
    field: "cogs_cost",
    format: "currency_negative",
    negate: true,
    expandable: false,
  },
  {
    key: "net_profit",
    label: "Net profit",
    field: "_net_profit",
    format: "currency",
    expandable: false,
    bold: true,
  },
  {
    key: "net_margin",
    label: "Net margin",
    field: "_net_margin",
    format: "percent",
    expandable: false,
    bold: true,
  },
];

function formatValue(
  value,
  format,
  convertCurrency = (v) => v,
  getCurrencySymbol = () => "$"
) {
  if (value === undefined || value === null || value === "") {
    return format === "percent"
      ? "0.00%"
      : format === "number"
      ? "0"
      : `${getCurrencySymbol()}0.00`;
  }

  const num = Number(value);
  if (isNaN(num)) return value;

  switch (format) {
    case "currency":
      return (
        getCurrencySymbol() +
        Number(convertCurrency(num)).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    case "currency_negative":
      return (
        (num < 0 ? `-${getCurrencySymbol()}` : getCurrencySymbol()) +
        Number(convertCurrency(Math.abs(num))).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    case "number":
      return num.toLocaleString("en-US");
    case "percent":
      return num.toFixed(2) + "%";
    default:
      return num.toLocaleString("en-US");
  }
}

function getMonthKey(year, monthIndex) {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
}

function computeDerivedFields(monthData) {
  const sales = monthData.orderedProductSales || 0;
  const promos = monthData.item_promotion_discount || 0;
  const refunds = monthData.refunded_amount || 0;
  const referralFee = monthData.referral_fee || 0;
  const fulfillmentFee = monthData.fulfillment_fee || 0;
  const extraFee = monthData.extra_fee || 0;
  const cogs = monthData.cogs_cost || 0;
  const productCost = monthData.product_cost || 0;
  const brandCost = monthData.brand_cost || 0;
  const displayCost = monthData.display_cost || 0;
  const indirectExpenses = monthData.indirect_expenses || 0;

  monthData._advertising_cost = productCost + brandCost + displayCost;
  monthData._amazon_fees = -(fulfillmentFee + referralFee) + extraFee;

  const netProfit =
    sales -
    promos -
    refunds -
    referralFee -
    fulfillmentFee -
    cogs -
    productCost -
    brandCost -
    displayCost +
    extraFee;

  monthData._gross_profit = netProfit + indirectExpenses;
  monthData._net_profit = netProfit;
  monthData._net_margin = sales ? (netProfit / sales) * 100 : 0;
}

function normalizeDesc(description) {
  if (
    !description ||
    /^\d+$/.test(description.trim()) ||
    description.trim().toLowerCase() === "fee"
  ) {
    return "Other";
  }
  const match = description.match(
    /^(.+\b(?:Fee|Discount|Deal|Reimbursement))\b[\s:\-–—]/i
  );
  if (match) return match[1];
  const dealsMatch = description.match(/^(Deals)[- ]/i);
  if (dealsMatch) return dealsMatch[1];
  return description;
}

const CANONICAL_INVENTORY_FEE_TYPE = "FBA Inventory Fee";
const CANONICAL_TRANSACTION_FEE_TYPE = "FBA Transaction fees";

const FEE_TYPE_ALIASES = {
  "Amazon Fees": "Deal fees",
  0: "Deal fees",
  "FBA Transaction fee": CANONICAL_TRANSACTION_FEE_TYPE,
  "FBA Transaction Fee": CANONICAL_TRANSACTION_FEE_TYPE,
  "FBA Transaction Fees": CANONICAL_TRANSACTION_FEE_TYPE,
  "FBA Inventory Fees": CANONICAL_INVENTORY_FEE_TYPE,
};

const EXTRA_FEE_DESC_REMAPS = [
  {
    test: /long[\s-]*term[\s-]*stor/i,
    feeType: CANONICAL_INVENTORY_FEE_TYPE,
    key: "Long-Term Storage Fee",
  },
  {
    test: /inbound[\s-]*placement/i,
    feeType: CANONICAL_TRANSACTION_FEE_TYPE,
    key: "FBA Inbound Placement Service Fee",
  },
  {
    test: /removal\s+order[:\s-]*disposal/i,
    feeType: CANONICAL_INVENTORY_FEE_TYPE,
    key: "FBA Removal Order: Disposal Fee",
  },
  {
    // Amazon named the same monthly charge "FBAStorageBilling" / "FBA storage fee" until 2026-06.
    test: /inventory\s*storage|^\s*fba\s*storage\s*(?:fee|billing)\s*$/i,
    feeType: CANONICAL_INVENTORY_FEE_TYPE,
    key: "FBA Inventory Storage Fee",
  },
];

function normalizeFeeType(feeType) {
  return FEE_TYPE_ALIASES[feeType] || feeType;
}

function canonicalizeExtraFee(feeType, description) {
  const key = normalizeDesc(description);
  const haystacks = [description || "", key];
  const remap = EXTRA_FEE_DESC_REMAPS.find(({ test }) =>
    haystacks.some((text) => test.test(text))
  );
  if (remap) {
    return { feeType: remap.feeType, key: remap.key };
  }
  return { feeType, key };
}

function collectExtraFeesDesc(dayData, breakdown) {
  const descEntries = dayData.extra_fees_desc;
  if (!Array.isArray(descEntries)) return;
  for (const entry of descEntries) {
    if (!Array.isArray(entry) || entry.length < 3) continue;
    const [amount, rawFeeType, description] = entry;
    const num = Number(amount) || 0;
    if (num === 0) continue;
    const { feeType, key } = canonicalizeExtraFee(
      normalizeFeeType(rawFeeType),
      description
    );
    if (!breakdown[feeType]) {
      breakdown[feeType] = { total: 0, items: {} };
    }
    breakdown[feeType].total += num;
    breakdown[feeType].items[key] = (breakdown[feeType].items[key] || 0) + num;
  }
}

function mergeExtraFeeBreakdowns(target, source) {
  for (const [feeType, data] of Object.entries(source)) {
    if (!target[feeType]) {
      target[feeType] = { total: 0, items: {} };
    }
    target[feeType].total += data.total;
    for (const [desc, val] of Object.entries(data.items)) {
      target[feeType].items[desc] = (target[feeType].items[desc] || 0) + val;
    }
  }
}

function filterDateKeys(keys, monthKey) {
  const monthPart = monthKey.split("-").slice(-1)[0] + "-";
  const fullPrefix = monthKey + "-";
  return keys.filter(
    (k) => k === monthKey || k.startsWith(fullPrefix) || k.startsWith(monthPart)
  );
}

function aggregateMonthlyData(yearlyData, months, selectedAsins) {
  const aggregated = {};

  for (const monthKey of months) {
    const monthData = {};
    RAW_FIELDS.forEach((f) => {
      monthData[f] = 0;
    });
    const extraFeeBreakdown = {};

    for (const asin of Object.keys(yearlyData)) {
      if (asin === "0") continue;
      if (selectedAsins.length > 0 && !selectedAsins.includes(asin)) continue;
      const asinData = yearlyData[asin];
      if (!asinData) continue;

      const dateKeys = filterDateKeys(Object.keys(asinData), monthKey);
      for (const dateKey of dateKeys) {
        const dayData = asinData[dateKey];
        if (!dayData) continue;

        RAW_FIELDS.forEach((f) => {
          if (dayData[f] !== undefined && dayData[f] !== null) {
            monthData[f] += Number(dayData[f]) || 0;
          }
        });
        collectExtraFeesDesc(dayData, extraFeeBreakdown);
      }
    }

    if (selectedAsins.length === 0) {
      const storeLevelData = yearlyData["0"];
      if (storeLevelData) {
        const dateKeys = filterDateKeys(Object.keys(storeLevelData), monthKey);
        for (const dateKey of dateKeys) {
          const dayData = storeLevelData[dateKey];
          if (!dayData) continue;
          // The '0' bucket carries every unattributed cost (extra fees via
          // COALESCE, brand spend for campaigns with no purchased-product
          // attribution), so sum all fields — the rest are 0 by construction.
          RAW_FIELDS.forEach((f) => {
            if (dayData[f] !== undefined && dayData[f] !== null) {
              monthData[f] += Number(dayData[f]) || 0;
            }
          });
          collectExtraFeesDesc(dayData, extraFeeBreakdown);
        }
      }
    }

    monthData._extra_fee_breakdown = extraFeeBreakdown;
    computeDerivedFields(monthData);
    aggregated[monthKey] = monthData;
  }

  const yearlyTotal = {};
  RAW_FIELDS.forEach((f) => {
    yearlyTotal[f] = 0;
  });
  const yearlyExtraFeeBreakdown = {};
  for (const monthKey of months) {
    RAW_FIELDS.forEach((f) => {
      yearlyTotal[f] += aggregated[monthKey]?.[f] || 0;
    });
    mergeExtraFeeBreakdowns(
      yearlyExtraFeeBreakdown,
      aggregated[monthKey]?._extra_fee_breakdown || {}
    );
  }
  yearlyTotal._extra_fee_breakdown = yearlyExtraFeeBreakdown;
  computeDerivedFields(yearlyTotal);
  aggregated._yearly_total = yearlyTotal;

  return aggregated;
}

function mergeExtraFeesDesc(a, b) {
  const aArr = Array.isArray(a) ? a : [];
  const bArr = Array.isArray(b) ? b : [];
  return [...aArr, ...bArr];
}

function mergeDayData(a, b) {
  if (!a) return { ...b };
  if (!b) return { ...a };
  const merged = { ...a };
  for (const [key, val] of Object.entries(b)) {
    if (key === "extra_fees_desc") {
      merged[key] = mergeExtraFeesDesc(merged[key], val);
    } else if (typeof val === "number" || RAW_FIELDS.includes(key)) {
      merged[key] = (Number(merged[key]) || 0) + (Number(val) || 0);
    } else if (merged[key] === undefined || merged[key] === null) {
      merged[key] = val;
    }
  }
  return merged;
}

function mergeYearlyDataSources(sources) {
  const merged = {};
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    for (const asin of Object.keys(src)) {
      const asinData = src[asin];
      if (!asinData || typeof asinData !== "object") continue;
      if (!merged[asin]) merged[asin] = {};
      for (const dateKey of Object.keys(asinData)) {
        merged[asin][dateKey] = mergeDayData(
          merged[asin][dateKey],
          asinData[dateKey]
        );
      }
    }
  }
  return merged;
}

function getMonthLabel(monthKey) {
  const [year, month] = monthKey.split("-");
  const monthIndex = parseInt(month, 10) - 1;
  const currentMonth = moment().month();
  const currentYear = moment().year();
  const currentDay = moment().date();

  if (parseInt(year) === currentYear && monthIndex === currentMonth) {
    return `1-${currentDay} ${MONTH_NAMES[monthIndex]} ${year}`;
  }
  return `${MONTH_NAMES[monthIndex]}`;
}

function SubRow({ sub, months, aggregatedData, expandAll }) {
  const { convertCurrency, getCurrencySymbol } = useCurrency();
  const [expanded, setExpanded] = useState(!!sub.dynamicBreakdownField);

  useEffect(() => {
    if (expandAll !== null) setExpanded(expandAll);
  }, [expandAll]);

  const breakdownGroups = useMemo(() => {
    if (!sub.dynamicBreakdownField) return [];
    const allTypes = new Set();
    const yearlyBreakdown =
      aggregatedData._yearly_total?.[sub.dynamicBreakdownField] || {};
    Object.keys(yearlyBreakdown).forEach((t) => allTypes.add(t));
    for (const monthKey of months) {
      const mb = aggregatedData[monthKey]?.[sub.dynamicBreakdownField] || {};
      Object.keys(mb).forEach((t) => allTypes.add(t));
    }
    return Array.from(allTypes)
      .sort((a, b) => {
        const aTotal = Math.abs(yearlyBreakdown[a]?.total || 0);
        const bTotal = Math.abs(yearlyBreakdown[b]?.total || 0);
        return bTotal - aTotal;
      })
      .map((feeType) => {
        const allDescs = new Set();
        const yearlyItems = yearlyBreakdown[feeType]?.items || {};
        Object.keys(yearlyItems).forEach((d) => allDescs.add(d));
        for (const monthKey of months) {
          const mb =
            aggregatedData[monthKey]?.[sub.dynamicBreakdownField]?.[feeType]
              ?.items || {};
          Object.keys(mb).forEach((d) => allDescs.add(d));
        }
        return { feeType, descriptions: Array.from(allDescs).sort() };
      });
  }, [sub.dynamicBreakdownField, aggregatedData, months]);

  const hasBreakdown = sub.dynamicBreakdownField && breakdownGroups.length > 0;

  const subCellSx = {
    textAlign: "right",
    py: 0.5,
    px: 1.5,
    fontSize: "0.78rem",
    color: "text.secondary",
    whiteSpace: "nowrap",
    borderBottom: "1px solid",
    borderColor: "divider",
    minWidth: 120,
  };

  return (
    <>
      <TableRow>
        <TableCell
          sx={{
            textAlign: "left",
            pl: hasBreakdown ? 6 : 8,
            py: 0.5,
            fontSize: "0.78rem",
            color: "text.secondary",
            fontWeight: 400,
            position: "sticky",
            left: 0,
            backgroundColor: "#fff",
            zIndex: 1,
            minWidth: 180,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {hasBreakdown ? (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ mr: 0.5, p: 0 }}
              >
                {expanded ? (
                  <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                ) : (
                  <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            ) : null}
            {sub.label}
          </Box>
        </TableCell>
        <TableCell
          sx={{
            ...subCellSx,
            fontWeight: 600,
            borderRight: "2px solid",
            borderRightColor: "divider",
            backgroundColor: "#f5f8ff",
          }}
        >
          {formatValue(
            sub.negate
              ? -(aggregatedData._yearly_total?.[sub.field] || 0)
              : aggregatedData._yearly_total?.[sub.field],
            sub.format,
            convertCurrency,
            getCurrencySymbol
          )}
        </TableCell>
        {months.map((monthKey) => (
          <TableCell key={monthKey} sx={subCellSx}>
            {formatValue(
              sub.negate
                ? -(aggregatedData[monthKey]?.[sub.field] || 0)
                : aggregatedData[monthKey]?.[sub.field],
              sub.format,
              convertCurrency,
              getCurrencySymbol
            )}
          </TableCell>
        ))}
      </TableRow>
      {hasBreakdown &&
        expanded &&
        breakdownGroups.map(({ feeType, descriptions }) => (
          <ExtraFeeGroup
            key={feeType}
            feeType={feeType}
            descriptions={descriptions}
            months={months}
            aggregatedData={aggregatedData}
            dynamicField={sub.dynamicBreakdownField}
            format={sub.format}
            subCellSx={subCellSx}
            expandAll={expandAll}
          />
        ))}
    </>
  );
}

function ExtraFeeGroup({
  feeType,
  descriptions,
  months,
  aggregatedData,
  dynamicField,
  format,
  subCellSx,
  expandAll,
}) {
  const { convertCurrency, getCurrencySymbol } = useCurrency();
  const [expanded, setExpanded] = useState(
    !feeType.toLowerCase().includes("liquidation")
  );

  useEffect(() => {
    if (expandAll !== null) setExpanded(expandAll);
  }, [expandAll]);
  const hasMultipleItems = descriptions.length > 1;

  function getGroupTotal(dataSlice) {
    return dataSlice?.[dynamicField]?.[feeType]?.total || 0;
  }

  function getItemValue(dataSlice, desc) {
    return dataSlice?.[dynamicField]?.[feeType]?.items?.[desc] || 0;
  }

  function stripPrefix(desc) {
    const prefix = feeType.replace(/\s*(Fee|Order|Fees)$/i, "").trim();
    const re = new RegExp(`^${prefix}\\s*`, "i");
    const stripped = desc.replace(re, "");
    if (stripped && stripped !== desc && stripped.length > 2) return stripped;
    return desc.replace(/^FBA\s+/i, "") || desc;
  }

  return (
    <>
      <TableRow>
        <TableCell
          sx={{
            textAlign: "left",
            pl: hasMultipleItems ? 10 : 12,
            py: 0.3,
            fontSize: "0.74rem",
            color: "text.disabled",
            fontWeight: hasMultipleItems ? 500 : 400,
            position: "sticky",
            left: 0,
            backgroundColor: "#fff",
            zIndex: 1,
            minWidth: 180,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {hasMultipleItems ? (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ mr: 0.5, p: 0 }}
              >
                {expanded ? (
                  <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                ) : (
                  <KeyboardArrowRightIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            ) : null}
            <Tooltip title={feeType} arrow>
              <Box
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 120,
                }}
              >
                {feeType}
              </Box>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell
          sx={{
            ...subCellSx,
            fontSize: "0.74rem",
            color: "text.disabled",
            fontWeight: 600,
            borderRight: "2px solid",
            borderRightColor: "divider",
            backgroundColor: "#f5f8ff",
          }}
        >
          {formatValue(
            getGroupTotal(aggregatedData._yearly_total),
            format,
            convertCurrency,
            getCurrencySymbol
          )}
        </TableCell>
        {months.map((monthKey) => (
          <TableCell
            key={monthKey}
            sx={{ ...subCellSx, fontSize: "0.74rem", color: "text.disabled" }}
          >
            {formatValue(
              getGroupTotal(aggregatedData[monthKey]),
              format,
              convertCurrency,
              getCurrencySymbol
            )}
          </TableCell>
        ))}
      </TableRow>
      {hasMultipleItems &&
        expanded &&
        descriptions.map((desc) => (
          <TableRow key={desc}>
            <TableCell
              sx={{
                textAlign: "left",
                pl: 15,
                py: 0.2,
                fontSize: "0.72rem",
                color: "text.disabled",
                fontWeight: 400,
                position: "sticky",
                left: 0,
                backgroundColor: "#fff",
                zIndex: 1,
                minWidth: 180,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Tooltip title={desc} arrow>
                <Box
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 100,
                  }}
                >
                  {stripPrefix(desc)}
                </Box>
              </Tooltip>
            </TableCell>
            <TableCell
              sx={{
                ...subCellSx,
                fontSize: "0.72rem",
                color: "text.disabled",
                fontWeight: 600,
                borderRight: "2px solid",
                borderRightColor: "divider",
                backgroundColor: "#f5f8ff",
              }}
            >
              {formatValue(
                getItemValue(aggregatedData._yearly_total, desc),
                format,
                convertCurrency,
                getCurrencySymbol
              )}
            </TableCell>
            {months.map((monthKey) => (
              <TableCell
                key={monthKey}
                sx={{
                  ...subCellSx,
                  fontSize: "0.72rem",
                  color: "text.disabled",
                }}
              >
                {formatValue(
                  getItemValue(aggregatedData[monthKey], desc),
                  format,
                  convertCurrency,
                  getCurrencySymbol
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
    </>
  );
}

function PnLRow({ row, months, aggregatedData, isEven, expandAll }) {
  const { convertCurrency, getCurrencySymbol } = useCurrency();
  const [expanded, setExpanded] = useState(row.expandable);

  useEffect(() => {
    if (expandAll !== null && row.expandable) setExpanded(expandAll);
  }, [expandAll, row.expandable]);

  const cellSx = {
    textAlign: "right",
    py: 1,
    px: 1.5,
    fontSize: "0.82rem",
    fontWeight: row.bold ? 600 : 400,
    whiteSpace: "nowrap",
    borderBottom: "1px solid",
    borderColor: "divider",
  };

  const labelSx = {
    ...cellSx,
    textAlign: "left",
    fontWeight: row.bold ? 700 : 500,
    position: "sticky",
    left: 0,
    backgroundColor: isEven ? "#fafafa" : "#fff",
    zIndex: 1,
    minWidth: 180,
  };

  return (
    <>
      <TableRow sx={{ backgroundColor: isEven ? "#fafafa" : "#fff" }}>
        <TableCell sx={labelSx}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {row.expandable ? (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ mr: 0.5, p: 0, minWidth: 20, width: 20 }}
              >
                {expanded ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 20, minWidth: 20, mr: 0.5 }} />
            )}
            {row.label}
          </Box>
        </TableCell>
        <TableCell
          sx={{
            ...cellSx,
            fontWeight: 700,
            backgroundColor: isEven ? "#f0f4ff" : "#f5f8ff",
            borderRight: "2px solid",
            borderRightColor: "divider",
          }}
        >
          {formatValue(
            row.negate
              ? -(aggregatedData._yearly_total?.[row.field] || 0)
              : aggregatedData._yearly_total?.[row.field],
            row.format,
            convertCurrency,
            getCurrencySymbol
          )}
        </TableCell>
        {months.map((monthKey) => (
          <TableCell key={monthKey} sx={cellSx}>
            {formatValue(
              row.negate
                ? -(aggregatedData[monthKey]?.[row.field] || 0)
                : aggregatedData[monthKey]?.[row.field],
              row.format,
              convertCurrency,
              getCurrencySymbol
            )}
          </TableCell>
        ))}
      </TableRow>
      {row.expandable &&
        row.subRows &&
        expanded &&
        row.subRows.map((sub) => (
          <SubRow
            key={sub.field}
            sub={sub}
            months={months}
            aggregatedData={aggregatedData}
            expandAll={expandAll}
          />
        ))}
    </>
  );
}

function StorePnL({ store, marketplace }) {
  const dispatch = useDispatch();
  const authorizedStores = useStores();
  const currentYear = moment().year();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedAsins, setSelectedAsins] = useState([]);
  const [expandAll, setExpandAll] = useState(null);
  const [viewMode, setViewMode] = useState("monthly");
  const [selectedMonth, setSelectedMonth] = useState(
    () => `${currentYear}-${String(moment().month() + 1).padStart(2, "0")}`
  );
  // All-stores consolidated P&L is expensive — only fetch after explicit user action.
  const [loadConsolidated, setLoadConsolidated] = useState(false);

  const { productProfitabilityYearlyData, productProfitabilityYearlyLoading } =
    useSelector(storeAnalyticsSelector);

  const isAllStores = store === "all";
  const apiStore = useMemo(
    () => (store ? store.replace("_client", "") : store),
    [store]
  );

  const isUnified = marketplace === "all";
  const unifiedMarketplaces = useMemo(() => ["us", "ca"], []);
  const storesToFetch = useMemo(() => {
    if (!store) return [];
    if (!isAllStores) {
      return [{ value: store }];
    }
    return authorizedStores
      .filter((option) => option.value && option.value !== "all")
      .map((option) => ({
        value: option.value,
      }));
  }, [authorizedStores, isAllStores, store]);
  const marketplacesToFetch = useMemo(
    () => (isUnified ? unifiedMarketplaces : [marketplace || "us"]),
    [isUnified, marketplace, unifiedMarketplaces]
  );
  const usesCombinedData = isAllStores || isUnified;
  const shouldFetchCombined =
    usesCombinedData && (!isAllStores || loadConsolidated);

  const [productItemsMap, setProductItemsMap] = useState({});
  const [combinedYearlyData, setCombinedYearlyData] = useState(null);
  const [combinedLoading, setCombinedLoading] = useState(false);
  // Per store × marketplace × endpoint status for All Stores loading UI.
  const [fetchStatuses, setFetchStatuses] = useState([]);

  const storeLabelByValue = useMemo(() => {
    const map = {};
    authorizedStores.forEach((option) => {
      if (option.value) map[option.value] = option.label || option.value;
    });
    return map;
  }, [authorizedStores]);

  const markFetchStatus = useCallback((key, status) => {
    setFetchStatuses((prev) =>
      prev.map((entry) => (entry.key === key ? { ...entry, status } : entry))
    );
  }, []);

  const initFetchStatuses = useCallback(
    (endpoints) => {
      const nextEntries = storesToFetch.flatMap((storeConfig) =>
        marketplacesToFetch.flatMap((mp) =>
          endpoints.map((endpoint) => ({
            key: makeFetchKey(endpoint, storeConfig.value, mp),
            store: storeConfig.value,
            storeLabel:
              storeLabelByValue[storeConfig.value] || storeConfig.value,
            marketplace: mp,
            endpoint,
            endpointLabel: FETCH_ENDPOINTS[endpoint] || endpoint,
            status: "pending",
          }))
        )
      );
      setFetchStatuses((prev) => {
        const keep = prev.filter(
          (entry) => !endpoints.includes(entry.endpoint)
        );
        return [...keep, ...nextEntries];
      });
    },
    [marketplacesToFetch, storeLabelByValue, storesToFetch]
  );

  useEffect(() => {
    setLoadConsolidated(false);
    setCombinedYearlyData(null);
    setSelectedAsins([]);
    setFetchStatuses([]);
  }, [store]);

  useEffect(() => {
    if (!store || storesToFetch.length === 0) {
      setProductItemsMap({});
      return;
    }
    if (isAllStores && !loadConsolidated) {
      setProductItemsMap({});
      return;
    }
    let cancelled = false;

    if (isAllStores) {
      initFetchStatuses(["productItems"]);
    }

    // Descriptive fields: prefer the first non-empty value (US first, CA second).
    // Inventory-style fields are summed across marketplaces.
    const SUMMABLE_FIELDS = new Set([
      "fba_stock",
      "inbound_stock",
      "reserved_stock",
      "available_stock",
      "total_stock",
      "sellable_stock",
      "working_stock",
    ]);

    const marketplaceIdToName = {
      ATVPDKIKX0DER: "us",
      A2EUQ1WTGCTBG2: "ca",
    };

    const mergeItemIntoMap = (map, item, mpHint) => {
      if (!item.asin) return;
      const mpName = marketplaceIdToName[item.marketplace_id] || mpHint || null;
      const existing = map[item.asin];
      if (!existing) {
        map[item.asin] = {
          ...item,
          _marketplaces: mpName ? [mpName] : [],
        };
        return;
      }
      if (!existing._marketplaces) existing._marketplaces = [];
      if (mpName && !existing._marketplaces.includes(mpName)) {
        existing._marketplaces.push(mpName);
      }
      Object.keys(item).forEach((k) => {
        const incoming = item[k];
        if (SUMMABLE_FIELDS.has(k)) {
          const a = Number(existing[k]) || 0;
          const b = Number(incoming) || 0;
          existing[k] = a + b;
        } else if (
          existing[k] === undefined ||
          existing[k] === null ||
          existing[k] === ""
        ) {
          existing[k] = incoming;
        }
      });
    };

    const fetchItems = async () => {
      try {
        const results = await Promise.all(
          storesToFetch.flatMap((storeConfig) =>
            marketplacesToFetch.map(async (mp) => {
              const key = makeFetchKey("productItems", storeConfig.value, mp);
              try {
                const result = await call_api_auth(
                  `${
                    atomicConfig.storeManagementServiceUrl
                  }/fetch-product-items-by-asins/${storeConfig.value.replace(
                    "_client",
                    ""
                  )}`,
                  "POST",
                  JSON.stringify({
                    marketplace: mp,
                  })
                );
                if (!cancelled && isAllStores) markFetchStatus(key, "done");
                return { result, mp, store: storeConfig.value };
              } catch (err) {
                console.warn(
                  `[StorePnL] fetch-product-items-by-asins(${storeConfig.value}, ${mp}) failed:`,
                  err
                );
                if (!cancelled && isAllStores) markFetchStatus(key, "error");
                return {
                  result: { data: { product_items: [] } },
                  mp,
                  store: storeConfig.value,
                };
              }
            })
          )
        );
        if (cancelled) return;

        const map = {};
        results.forEach(({ result, mp, store: resultStore }) => {
          const items = result?.data?.product_items || [];
          if (usesCombinedData) {
            console.info(
              `[StorePnL] product items (store=${resultStore}, marketplace=${mp}): ${items.length}`
            );
          }
          items.forEach((item) => mergeItemIntoMap(map, item, mp));
        });

        setProductItemsMap(map);
      } catch {
        if (!cancelled) setProductItemsMap({});
      }
    };

    fetchItems();
    return () => {
      cancelled = true;
    };
  }, [
    initFetchStatuses,
    isAllStores,
    loadConsolidated,
    markFetchStatus,
    marketplacesToFetch,
    store,
    storesToFetch,
    usesCombinedData,
  ]);

  const effectiveYearlyData = useMemo(
    () =>
      usesCombinedData
        ? combinedYearlyData || {}
        : productProfitabilityYearlyData,
    [combinedYearlyData, productProfitabilityYearlyData, usesCombinedData]
  );

  const allStoresFetchProgress = useMemo(() => {
    const total = fetchStatuses.length;
    if (!total) {
      return { completed: 0, total: 0, percent: 0, done: false };
    }
    const completed = fetchStatuses.filter(
      (entry) => entry.status === "done" || entry.status === "error"
    ).length;
    return {
      completed,
      total,
      percent: (completed / total) * 100,
      done: completed === total,
    };
  }, [fetchStatuses]);

  const effectiveLoading = usesCombinedData
    ? isAllStores
      ? loadConsolidated &&
        (fetchStatuses.length === 0 || !allStoresFetchProgress.done)
      : combinedLoading
    : productProfitabilityYearlyLoading;

  const availableAsins = useMemo(() => {
    const asinSet = new Set();
    if (effectiveYearlyData && typeof effectiveYearlyData === "object") {
      Object.keys(effectiveYearlyData).forEach((k) => {
        if (k !== "0") asinSet.add(k);
      });
    }
    if (productItemsMap && typeof productItemsMap === "object") {
      Object.keys(productItemsMap).forEach((k) => {
        if (k && k !== "0") asinSet.add(k);
      });
    }
    return Array.from(asinSet).sort();
  }, [effectiveYearlyData, productItemsMap]);

  const fetchData = useCallback(() => {
    if (!store || usesCombinedData) return;
    dispatch(
      fetchProductProfitabilityYearly({
        store: apiStore,
        marketplace: marketplace || "us",
        year: selectedYear,
      })
    );
  }, [dispatch, store, apiStore, marketplace, selectedYear, usesCombinedData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!shouldFetchCombined || !store || storesToFetch.length === 0) {
      if (!usesCombinedData) setCombinedYearlyData(null);
      return;
    }

    let cancelled = false;
    const fetchCombined = async () => {
      setCombinedLoading(true);
      if (isAllStores) {
        initFetchStatuses(["profitability"]);
      }
      try {
        const results = await Promise.all(
          storesToFetch.flatMap((storeConfig) =>
            marketplacesToFetch.map(async (mp) => {
              const key = makeFetchKey("profitability", storeConfig.value, mp);
              const requestStore = storeConfig.value.replace("_client", "");
              const url = `${atomicConfig.storeManagementServiceUrl}/fetch-products-profitability-yearly/${requestStore}?year=${selectedYear}&marketplace=${mp}`;
              try {
                const result = await call_api_auth(url, "GET");
                if (!cancelled && isAllStores) markFetchStatus(key, "done");
                return {
                  result,
                  mp,
                  store: storeConfig.value,
                };
              } catch (err) {
                console.warn(
                  `[StorePnL] profitability-yearly failed for ${storeConfig.value}, ${mp}:`,
                  err
                );
                if (!cancelled && isAllStores) markFetchStatus(key, "error");
                return {
                  result: { data: {} },
                  mp,
                  store: storeConfig.value,
                };
              }
            })
          )
        );
        if (cancelled) return;

        results.forEach(({ result, mp, store: resultStore }) => {
          const payload = result?.data || {};
          const asinCount = Object.keys(payload).filter(
            (k) => k !== "0"
          ).length;
          console.info(
            `[StorePnL] profitability yearly for store=${resultStore}, marketplace=${mp}: ${asinCount} ASINs`
          );
        });

        const merged = mergeYearlyDataSources(
          results.map(({ result }) => result?.data || {})
        );
        setCombinedYearlyData(merged);
      } catch {
        if (!cancelled) setCombinedYearlyData({});
      } finally {
        if (!cancelled) setCombinedLoading(false);
      }
    };

    fetchCombined();
    return () => {
      cancelled = true;
    };
  }, [
    initFetchStatuses,
    isAllStores,
    markFetchStatus,
    marketplacesToFetch,
    selectedYear,
    shouldFetchCombined,
    store,
    storesToFetch,
    usesCombinedData,
  ]);

  const months = useMemo(() => {
    const result = [];
    const currentMonth = moment().month();
    const isCurrentYear = selectedYear === currentYear;
    const lastMonth = isCurrentYear ? currentMonth : 11;

    for (let i = lastMonth; i >= 0; i--) {
      result.push(getMonthKey(selectedYear, i));
    }
    return result;
  }, [selectedYear, currentYear]);

  const byProductMonths = useMemo(() => {
    if (selectedMonth === "all") return months;
    return months.filter((m) => m === selectedMonth);
  }, [months, selectedMonth]);

  const aggregatedData = useMemo(() => {
    if (!effectiveYearlyData || typeof effectiveYearlyData !== "object") {
      return {};
    }
    return aggregateMonthlyData(effectiveYearlyData, months, selectedAsins);
  }, [effectiveYearlyData, months, selectedAsins]);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= currentYear - 3; y--) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  const exportCsv = useCallback(() => {
    if (!aggregatedData || Object.keys(aggregatedData).length === 0) return;

    const escCsv = (v) => {
      const s = String(v ?? "");
      return s.includes(",") || s.includes('"')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const rawValue = (val) => {
      if (val === undefined || val === null) return 0;
      const n = Number(val);
      return isNaN(n) ? 0 : Math.round(n * 100) / 100;
    };

    const HIERARCHY_LEVELS = 4;
    const hierarchyHeaders = [
      "Category",
      "SubCategory",
      "Fee Type",
      "Detail",
    ].slice(0, HIERARCHY_LEVELS);
    const header = [
      ...hierarchyHeaders,
      `${selectedYear} Total`,
      ...months.map((mk) => getMonthLabel(mk)),
    ];
    const rows = [header.map(escCsv).join(",")];

    const hierarchyCells = (labels) => {
      const cells = [];
      for (let i = 0; i < HIERARCHY_LEVELS; i++) {
        cells.push(escCsv(labels[i] || ""));
      }
      return cells;
    };

    const addRow = (labels, field, negate = false) => {
      const sign = negate ? -1 : 1;
      const cells = hierarchyCells(labels);
      cells.push(rawValue((aggregatedData._yearly_total?.[field] || 0) * sign));
      for (const mk of months) {
        cells.push(rawValue((aggregatedData[mk]?.[field] || 0) * sign));
      }
      rows.push(cells.join(","));
    };

    const addValueRow = (labels, getValue) => {
      const cells = hierarchyCells(labels);
      cells.push(rawValue(getValue(aggregatedData._yearly_total)));
      for (const mk of months) {
        cells.push(rawValue(getValue(aggregatedData[mk])));
      }
      rows.push(cells.join(","));
    };

    const addBreakdownValue = (dataSlice, dynamicField, feeType, desc) => {
      return dataSlice?.[dynamicField]?.[feeType]?.items?.[desc] || 0;
    };
    const addGroupTotal = (dataSlice, dynamicField, feeType) => {
      return dataSlice?.[dynamicField]?.[feeType]?.total || 0;
    };

    for (const row of PNL_ROW_CONFIG) {
      addRow([row.label], row.field, row.negate);
      if (row.subRows) {
        for (const sub of row.subRows) {
          addRow([row.label, sub.label], sub.field, sub.negate);

          if (sub.dynamicBreakdownField) {
            const breakdown =
              aggregatedData._yearly_total?.[sub.dynamicBreakdownField] || {};
            const allTypes = new Set(Object.keys(breakdown));
            for (const mk of months) {
              const mb = aggregatedData[mk]?.[sub.dynamicBreakdownField] || {};
              Object.keys(mb).forEach((t) => allTypes.add(t));
            }

            for (const feeType of Array.from(allTypes).sort()) {
              addValueRow([row.label, sub.label, feeType], (dataSlice) =>
                addGroupTotal(dataSlice, sub.dynamicBreakdownField, feeType)
              );

              const allDescs = new Set();
              const yearlyItems = breakdown[feeType]?.items || {};
              Object.keys(yearlyItems).forEach((d) => allDescs.add(d));
              for (const mk of months) {
                const items =
                  aggregatedData[mk]?.[sub.dynamicBreakdownField]?.[feeType]
                    ?.items || {};
                Object.keys(items).forEach((d) => allDescs.add(d));
              }

              for (const desc of Array.from(allDescs).sort()) {
                addValueRow(
                  [row.label, sub.label, feeType, desc],
                  (dataSlice) =>
                    addBreakdownValue(
                      dataSlice,
                      sub.dynamicBreakdownField,
                      feeType,
                      desc
                    )
                );
              }
            }
          }
        }
      }
    }

    const blob = new Blob([rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const firstMonth = months[months.length - 1];
    const lastMonth = months[0];
    const marketplaceLabel = isUnified ? "world" : marketplace || "us";
    a.download = `PNL-${store}-${marketplaceLabel}-${firstMonth}_${lastMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [aggregatedData, months, selectedYear, store, marketplace, isUnified]);

  // The cookie path has no consolidated P&L, so "All stores" asks for a store.
  if (!store || (isAllStores && isCookieAuth())) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Please select a store to view P&L data
        </Typography>
      </Box>
    );
  }

  if (isAllStores && !loadConsolidated) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <Typography variant="h6" color="text.secondary" textAlign="center">
          Consolidated P&L across all stores can take a while to load.
        </Typography>
        <Button
          variant="contained"
          onClick={() => setLoadConsolidated(true)}
          sx={{ mt: 3, textTransform: "none" }}
        >
          Load Consolidated PNL
        </Button>
      </Box>
    );
  }

  if (effectiveLoading) {
    if (isAllStores) {
      const sortedStatuses = [...fetchStatuses].sort((a, b) => {
        const storeCmp = (a.storeLabel || "").localeCompare(b.storeLabel || "");
        if (storeCmp !== 0) return storeCmp;
        const mpCmp = (a.marketplace || "").localeCompare(b.marketplace || "");
        if (mpCmp !== 0) return mpCmp;
        return (a.endpointLabel || "").localeCompare(b.endpointLabel || "");
      });

      return (
        <Box sx={{ width: "100%", py: 2 }}>
          <Box sx={{ width: "100%" }}>
            <Typography variant="body2" color="textSecondary">
              {`Loading: ${Math.round(allStoresFetchProgress.percent)}%`}
              {allStoresFetchProgress.total > 0
                ? ` (${allStoresFetchProgress.completed}/${allStoresFetchProgress.total})`
                : ""}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={allStoresFetchProgress.percent}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              mt: 3,
              maxHeight: "calc(100vh - 260px)",
              overflowY: "auto",
              pr: 1,
            }}
          >
            {sortedStatuses.map((entry) => (
              <Box
                key={entry.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor:
                    entry.status === "pending" ? "action.hover" : "transparent",
                }}
              >
                {entry.status === "pending" ? (
                  <CircularProgress size={14} />
                ) : entry.status === "error" ? (
                  <ErrorOutlineIcon color="error" sx={{ fontSize: 16 }} />
                ) : (
                  <CheckCircleOutlineIcon
                    color="success"
                    sx={{ fontSize: 16 }}
                  />
                )}
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {entry.storeLabel}
                  {" · "}
                  {(entry.marketplace || "").toUpperCase()}
                  {" · "}
                  {entry.endpointLabel}
                </Typography>
                <Typography
                  variant="caption"
                  color={
                    entry.status === "error"
                      ? "error"
                      : entry.status === "done"
                      ? "success.main"
                      : "text.secondary"
                  }
                >
                  {entry.status === "pending"
                    ? "Loading"
                    : entry.status === "error"
                    ? "Failed"
                    : "Done"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Profit & Loss Statement
          </Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, val) => val && setViewMode(val)}
            size="small"
          >
            <ToggleButton value="monthly" sx={{ textTransform: "none", px: 2 }}>
              Monthly P&L
            </ToggleButton>
            <ToggleButton
              value="byProduct"
              sx={{ textTransform: "none", px: 2 }}
            >
              By Product
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {viewMode === "monthly" && (
            <Autocomplete
              multiple
              size="small"
              options={availableAsins}
              value={selectedAsins}
              onChange={(_, newValue) => setSelectedAsins(newValue)}
              disableCloseOnSelect
              getOptionLabel={(option) => {
                const item = productItemsMap[option];
                return item?.product_name
                  ? `${option} - ${item.product_name}`
                  : option;
              }}
              renderOption={(props, option, { selected }) => {
                const item = productItemsMap[option];
                const marketplaces = item?._marketplaces || [];
                const flagFor = (mp) =>
                  mp === "us"
                    ? "🇺🇸"
                    : mp === "ca"
                    ? "🇨🇦"
                    : mp === "mx"
                    ? "🇲🇽"
                    : "";
                return (
                  <li {...props}>
                    <Checkbox
                      size="small"
                      checked={selected}
                      sx={{ mr: 0.5 }}
                    />
                    {item?.image && (
                      <Box
                        component="img"
                        src={item.image}
                        alt={option}
                        sx={{
                          width: 32,
                          height: 32,
                          objectFit: "contain",
                          mr: 1,
                          borderRadius: 0.5,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Box sx={{ overflow: "hidden" }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: 500 }}
                      >
                        {isUnified && marketplaces.length > 0 && (
                          <Box component="span" sx={{ mr: 0.5 }}>
                            {marketplaces.map(flagFor).join("")}
                          </Box>
                        )}
                        {option}
                      </Typography>
                      {item?.product_name && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: "block", maxWidth: 300 }}
                        >
                          {item.product_name}
                        </Typography>
                      )}
                      {item?.sku && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          noWrap
                          sx={{ display: "block", maxWidth: 300 }}
                        >
                          SKU: {item.sku}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const item = productItemsMap[option];
                  return (
                    <Chip
                      size="small"
                      avatar={
                        item?.image ? (
                          <Box
                            component="img"
                            src={item.image}
                            alt={option}
                            sx={{
                              width: 20,
                              height: 20,
                              objectFit: "contain",
                              borderRadius: "50%",
                            }}
                          />
                        ) : undefined
                      }
                      label={option}
                      {...getTagProps({ index })}
                    />
                  );
                })
              }
              filterOptions={(options, { inputValue }) => {
                const lower = inputValue.toLowerCase();
                return options.filter((asin) => {
                  if (asin.toLowerCase().includes(lower)) return true;
                  const item = productItemsMap[asin];
                  if (
                    item?.sku &&
                    String(item.sku).toLowerCase().includes(lower)
                  )
                    return true;
                  return item?.product_name?.toLowerCase().includes(lower);
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selectedAsins.length === 0 ? "All Products" : ""}
                />
              )}
              sx={{ minWidth: 350 }}
            />
          )}
          {viewMode === "byProduct" && (
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              size="small"
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">All Months</MenuItem>
              {months.map((mk) => {
                const monthIdx = parseInt(mk.split("-")[1], 10) - 1;
                return (
                  <MenuItem key={mk} value={mk}>
                    {MONTH_NAMES[monthIdx]}
                  </MenuItem>
                );
              })}
            </Select>
          )}
          <Select
            value={selectedYear}
            onChange={(e) => {
              const yr = e.target.value;
              setSelectedYear(yr);
              const lastMonthIdx = yr === currentYear ? moment().month() : 11;
              setSelectedMonth(
                `${yr}-${String(lastMonthIdx + 1).padStart(2, "0")}`
              );
            }}
            size="small"
            sx={{ minWidth: 100 }}
          >
            {yearOptions.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
          {viewMode === "monthly" && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={expandAll === true}
                    indeterminate={expandAll === null}
                    onChange={(e) => setExpandAll(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    Expand all
                  </Typography>
                }
                sx={{ mr: 0 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={exportCsv}
                sx={{ whiteSpace: "nowrap" }}
              >
                CSV
              </Button>
            </>
          )}
        </Box>
      </Box>

      {viewMode === "monthly" ? (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ overflowX: "auto" }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#fff",
                    zIndex: 3,
                    minWidth: 180,
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  Parameter/Date
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    borderBottom: "2px solid",
                    borderColor: "divider",
                    borderRight: "2px solid",
                    borderRightColor: "divider",
                    backgroundColor: "#f0f4ff",
                    minWidth: 120,
                  }}
                >
                  {selectedYear} Total
                </TableCell>
                {months.map((monthKey) => (
                  <TableCell
                    key={monthKey}
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      borderBottom: "2px solid",
                      borderColor: "divider",
                      minWidth: 120,
                    }}
                  >
                    {getMonthLabel(monthKey)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {PNL_ROW_CONFIG.map((row, idx) => (
                <PnLRow
                  key={row.key}
                  row={row}
                  months={months}
                  aggregatedData={aggregatedData}
                  isEven={idx % 2 === 0}
                  expandAll={expandAll}
                />
              ))}
              <TableRow>
                <TableCell
                  colSpan={months.length + 2}
                  sx={{ border: "none", py: 3 }}
                />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <PnLByProduct
          yearlyData={effectiveYearlyData}
          months={byProductMonths}
          productItemsMap={productItemsMap}
          selectedYear={selectedYear}
          store={store}
          marketplace={marketplace}
        />
      )}
    </Box>
  );
}

export { aggregateMonthlyData, mergeYearlyDataSources };
export default StorePnL;
